<script lang="ts">
  import { router } from '@inertiajs/svelte';
  import { onMount } from 'svelte';
  import type {
    Project,
    ProjectsByStatus,
    CalendarEvent,
    TablePeriod,
    DisplayFilter,
  } from '../lib/types';
  import { showProjects, showEvents } from '../lib/types';
  import {
    getShortMonthName,
    categoryIcons,
    statusAccentColors,
    eventsForMonth,
    formatCalendarDate,
    progressStyle,
    progressComplete,
    sortEventsForDisplay,
    formatEventTimeShort,
  } from '../lib/utils';
  import { showProjectTooltip, hideProjectTooltip } from '../lib/stores/projectTooltip';
  import { setupMaxVisible, MAX_VISIBLE_PRESETS } from '../lib/useMaxVisible';
  import {
    draggedProject,
    openProjectRail,
    openProjectModalForCreate,
    openMonthRail,
    openEventsRailForMonth,
    openEventModal,
    requestProjectMove,
  } from '../lib/stores/ui';
  import { showProjectCellPopover, hideProjectCellPopover } from '../lib/stores/projectCellPopover';
  import { viewport } from '../lib/stores/viewport';

  interface Props {
    visibleMonths: string[];
    projectsByMonth: Record<string, ProjectsByStatus>;
    events: CalendarEvent[];
    currentMonth: string;
    period: TablePeriod;
    display?: DisplayFilter | null;
  }

  let {
    visibleMonths,
    projectsByMonth,
    events,
    currentMonth,
    period = 'full',
    display = null,
  }: Props = $props();

  // Period options for the selector
  const periodOptions: { value: TablePeriod; label: string; shortLabel: string }[] = [
    { value: 'full', label: 'Full Year', shortLabel: '12mo' },
    { value: 'half', label: 'Half', shortLabel: '6mo' },
    { value: 'quarter', label: 'Quarter', shortLabel: '3mo' },
  ];

  function setPeriod(newPeriod: TablePeriod) {
    if (newPeriod === period) return;

    const params = new URLSearchParams();
    params.set('view', 'table');
    if (newPeriod !== 'full') {
      params.set('range', newPeriod);
    }

    // Extract year from currentMonth (format: "2026-01")
    const year = currentMonth.split('-')[0];
    router.get(`/year/${year}?${params.toString()}`, {}, { preserveScroll: true });
  }

  // Get all projects for a month as a flat list (ordered: active, planned, done)
  function getAllProjects(monthKey: string): Project[] {
    const data = projectsByMonth[monthKey] || { planned: [], active: [], almost: [], done: [] };
    return [...(data.active || []), ...(data.planned || []), ...(data.done || [])];
  }

  // Get events for a month
  function getMonthEvents(monthKey: string): CalendarEvent[] {
    return eventsForMonth(events, monthKey);
  }

  // Determine month state
  function getMonthState(monthKey: string): 'past' | 'current' | 'future' {
    if (monthKey === currentMonth) return 'current';
    if (monthKey < currentMonth) return 'past';
    return 'future';
  }

  function navigateToMonth(monthKey: string) {
    router.get(`/month/${monthKey}`, {}, { preserveScroll: true });
  }

  // Dynamic max items based on available height + responsive column count
  let monthGrid: HTMLElement;
  let maxVisibleProjects = $state<number>(MAX_VISIBLE_PRESETS.projectChipGrid.defaultVisible);
  let maxVisibleEvents = $state<number>(MAX_VISIBLE_PRESETS.eventChipGrid.defaultVisible);
  let projectColumns = $state<number>(4);
  let eventColumns = $state<number>(4);

  const MIN_COL_WIDTH = 100; // px per grid column before reducing count
  let projectMaxVisibleCleanup: (() => void) | null = null;
  let eventMaxVisibleCleanup: (() => void) | null = null;

  function setupProjectMaxVisible(container: HTMLElement, columns: number) {
    if (projectMaxVisibleCleanup) {
      projectMaxVisibleCleanup();
      projectMaxVisibleCleanup = null;
    }
    projectMaxVisibleCleanup = setupMaxVisible(
      container,
      {
        ...MAX_VISIBLE_PRESETS.projectChipGrid,
        columns,
      },
      (max) => {
        maxVisibleProjects = max;
      },
    );
  }

  function setupEventMaxVisible(container: HTMLElement, columns: number) {
    if (eventMaxVisibleCleanup) {
      eventMaxVisibleCleanup();
      eventMaxVisibleCleanup = null;
    }
    eventMaxVisibleCleanup = setupMaxVisible(
      container,
      {
        ...MAX_VISIBLE_PRESETS.eventChipGrid,
        columns,
      },
      (max) => {
        maxVisibleEvents = max;
      },
    );
  }

  onMount(() => {
    const cleanups: (() => void)[] = [];

    // Find the first row's containers for measurement
    const projectsGrid = monthGrid?.querySelector('.items-grid') as HTMLElement;
    const eventsGrid = monthGrid?.querySelectorAll('.items-grid')[1] as HTMLElement;

    // Responsive column count + max visible for projects
    if (projectsGrid) {
      const colObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          const w = projectsGrid.clientWidth;
          const newCols = Math.max(1, Math.min(4, Math.floor(w / MIN_COL_WIDTH)));
          if (newCols !== projectColumns) {
            projectColumns = newCols;
            setupProjectMaxVisible(projectsGrid, newCols);
          }
        });
      });
      colObserver.observe(projectsGrid);
      cleanups.push(() => colObserver.disconnect());

      setupProjectMaxVisible(projectsGrid, projectColumns);
    }

    // Responsive column count + max visible for events
    if (eventsGrid) {
      const colObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          const w = eventsGrid.clientWidth;
          const newCols = Math.max(1, Math.min(4, Math.floor(w / MIN_COL_WIDTH)));
          if (newCols !== eventColumns) {
            eventColumns = newCols;
            setupEventMaxVisible(eventsGrid, newCols);
          }
        });
      });
      colObserver.observe(eventsGrid);
      cleanups.push(() => colObserver.disconnect());

      setupEventMaxVisible(eventsGrid, eventColumns);
    }

    return () => {
      cleanups.forEach((fn) => fn());
      if (projectMaxVisibleCleanup) projectMaxVisibleCleanup();
      if (eventMaxVisibleCleanup) eventMaxVisibleCleanup();
    };
  });

  // Drag and drop
  let dropTarget = $state<string | null>(null);
  let draggingProjectId = $state<string | null>(null);

  function handleDragStart(e: DragEvent, project: Project) {
    hideProjectTooltip(true);
    draggingProjectId = project.id;
    draggedProject.set(project);
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(project.id));
    }
  }

  function handleDragEnd() {
    draggingProjectId = null;
    draggedProject.set(null);
  }

  function handleDragOver(e: DragEvent, monthKey: string) {
    e.preventDefault();
    if ($draggedProject) {
      dropTarget = monthKey;
    }
  }

  function handleDragLeave(e: DragEvent) {
    const relatedTarget = e.relatedTarget as Node;
    const currentTarget = e.currentTarget as Node;
    if (!currentTarget.contains(relatedTarget)) {
      dropTarget = null;
    }
  }

  function handleDrop(e: DragEvent, monthKey: string) {
    e.preventDefault();
    dropTarget = null;

    // Only process if a project (not an event) is being dragged
    if (!$draggedProject) return;

    const projectId =
      e.dataTransfer?.getData('application/project') || e.dataTransfer?.getData('text/plain');
    if (projectId) {
      requestProjectMove(projectId, $draggedProject, 'month', { monthKey, status: 'planned' });
    }
  }

  function getShortMonthWithYear(monthKey: string): string {
    const [year, month] = monthKey.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1));
    const monthStr = formatCalendarDate(date, { month: 'short' });
    return `${monthStr} ${year}`;
  }

  function formatEventDate(dateStr: string): string {
    const day = parseInt(dateStr.split('-')[2]);
    return String(day);
  }

  // Get first day of month for creating events
  function getMonthFirstDate(monthKey: string): string {
    return `${monthKey}-01`;
  }

  function handleAddProject(monthKey: string) {
    openProjectModalForCreate({
      schedulingState: 'scheduled',
      monthKey: monthKey,
      status: 'planned',
    });
  }

  function handleAddEvent(monthKey: string) {
    const date = getMonthFirstDate(monthKey);
    openEventModal(date);
  }

  // Double-click on empty area to add new item
  function handleProjectsDoubleClick(e: MouseEvent, monthKey: string) {
    const target = e.target as HTMLElement;
    // Only trigger if clicking on the container/grid background, not on items
    if (
      target.closest('.project-chip') ||
      target.closest('.more-chip') ||
      target.closest('.add-inline-btn')
    ) {
      return;
    }
    handleAddProject(monthKey);
  }

  function handleEventsDoubleClick(e: MouseEvent, monthKey: string) {
    const target = e.target as HTMLElement;
    // Only trigger if clicking on the container/grid background, not on items
    if (
      target.closest('.event-chip') ||
      target.closest('.more-chip') ||
      target.closest('.add-inline-btn')
    ) {
      return;
    }
    handleAddEvent(monthKey);
  }

  // Project cell popover: show on more-chip hover or blank area hover (when items hidden)
  function handleMoreChipEnter(e: MouseEvent, monthKey: string, projects: Project[]) {
    showProjectCellPopover(
      projects,
      getShortMonthWithYear(monthKey),
      e.currentTarget as HTMLElement,
    );
  }

  function handleMoreChipLeave() {
    hideProjectCellPopover();
  }
</script>

<div class="table-container">
  <!-- Header row -->
  <div class="table-header">
    <div class="header-cell month-col">Month</div>
    {#if showProjects(display)}
      <div class="header-cell projects-col">📋 Projects</div>
    {/if}
    {#if showEvents(display)}
      <div class="header-cell events-col">
        <span>📅 Events</span>
        <div class="period-selector">
          {#each periodOptions as opt}
            <button
              type="button"
              class="period-btn"
              class:active={period === opt.value}
              onclick={() => setPeriod(opt.value)}
              title="{opt.label} ({opt.shortLabel})"
            >
              {opt.label}
            </button>
          {/each}
        </div>
      </div>
    {/if}
  </div>

  <div class="year-grid" bind:this={monthGrid}>
    {#each visibleMonths as monthKey (monthKey)}
      {@const state = getMonthState(monthKey)}
      {@const isCurrent = state === 'current'}
      {@const isPast = state === 'past'}
      {@const isFuture = state === 'future'}
      {@const projects = getAllProjects(monthKey)}
      {@const monthEvents = getMonthEvents(monthKey)}
      {@const visibleProjects = projects.slice(0, maxVisibleProjects)}
      {@const hiddenProjectCount = Math.max(0, projects.length - maxVisibleProjects)}
      {@const visibleEvents = monthEvents.slice(0, maxVisibleEvents)}
      {@const hiddenEventCount = Math.max(0, monthEvents.length - maxVisibleEvents)}

      <div class="month-row" class:past={isPast} class:current={isCurrent} class:future={isFuture}>
        <!-- Month label -->
        <div class="month-col month-label">
          <button type="button" class="month-link" onclick={() => navigateToMonth(monthKey)}>
            <span class="month-name">{getShortMonthName(monthKey)}</span>
            <span class="month-year">{monthKey.split('-')[0]}</span>
          </button>
        </div>

        <!-- Projects column (drop target) -->
        {#if showProjects(display)}
          <div
            class="projects-col"
            class:drop-target={dropTarget === monthKey}
            role="group"
            ondragover={(e) => handleDragOver(e, monthKey)}
            ondragleave={handleDragLeave}
            ondrop={(e) => handleDrop(e, monthKey)}
            ondblclick={(e) => handleProjectsDoubleClick(e, monthKey)}
          >
            <div class="items-grid" style="grid-template-columns: repeat({projectColumns}, 1fr);">
              {#each visibleProjects as project (project.id)}
                <button
                  type="button"
                  class="project-chip {statusAccentColors[project.status]}"
                  style={progressStyle(project)}
                  data-progress-complete={progressComplete(project) || undefined}
                  class:dragging={draggingProjectId === project.id}
                  draggable={!$viewport.isMobile}
                  ondragstart={(e) => handleDragStart(e, project)}
                  ondragend={handleDragEnd}
                  onclick={() => {
                    hideProjectTooltip(true);
                    openProjectRail(project.id);
                  }}
                  onmouseenter={(e) => showProjectTooltip(project, e.currentTarget as HTMLElement)}
                  onmouseleave={() => hideProjectTooltip()}
                  title="{project.title} ({project.status}){project.multiWeek
                    ? ` — ${project.totalWeeks} weeks`
                    : ''}"
                >
                  <span class="chip-category">{categoryIcons[project.category]}</span>
                  {#if project.isPriority}
                    <span class="chip-star">★</span>
                  {/if}
                  <span class="chip-title">{project.title}</span>
                  {#if project.multiWeek && project.totalWeeks}
                    <span class="chip-span">{project.totalWeeks}w</span>
                  {/if}
                </button>
              {/each}
              {#if hiddenProjectCount > 0}
                <span
                  class="more-chip"
                  role="note"
                  onmouseenter={(e) => handleMoreChipEnter(e, monthKey, projects)}
                  onmouseleave={handleMoreChipLeave}
                >
                  +{hiddenProjectCount} more
                </span>
              {/if}
              {#if projects.length === 0}
                <span class="empty-cell">{isFuture ? 'No targets' : '—'}</span>
              {/if}
            </div>
            <button
              type="button"
              class="add-inline-btn hover-reveal"
              onclick={() => handleAddProject(monthKey)}
              title="Add project"
            >
              +
            </button>
          </div>
        {/if}

        <!-- Events column -->
        {#if showEvents(display)}
          <div
            class="events-col"
            role="group"
            ondblclick={(e) => handleEventsDoubleClick(e, monthKey)}
          >
            <div class="items-grid" style="grid-template-columns: repeat({eventColumns}, 1fr);">
              {#each visibleEvents as event (event.id)}
                {@const timeStr = formatEventTimeShort(event)}
                <div
                  class="event-chip event-{event.type}"
                  title="{timeStr ? `${timeStr} ` : ''}{event.title} ({event.date})"
                >
                  <span class="event-date"
                    >{formatEventDate(event.date)}{#if timeStr}
                      · {timeStr}{/if}</span
                  >
                  <span class="chip-title">{event.title}</span>
                </div>
              {/each}
              {#if hiddenEventCount > 0}
                <button
                  type="button"
                  class="more-chip"
                  onclick={() => openEventsRailForMonth(monthKey, getShortMonthWithYear(monthKey))}
                >
                  +{hiddenEventCount} more
                </button>
              {/if}
              {#if monthEvents.length === 0}
                <span class="empty-cell">—</span>
              {/if}
            </div>
            <button
              type="button"
              class="add-inline-btn hover-reveal"
              onclick={() => handleAddEvent(monthKey)}
              title="Add event"
            >
              +
            </button>
          </div>
        {/if}
      </div>
    {/each}
  </div>
</div>

<style>
  .table-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 0.375rem;
    background: #f9fafb;
  }

  /* Header row */
  .table-header {
    display: flex;
    align-items: stretch;
    background: white;
    border-radius: 0.375rem 0.375rem 0 0;
    border-bottom: 1px solid #e5e7eb;
    flex-shrink: 0;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  .header-cell {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.375rem 0.5rem;
    font-size: 0.625rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  .header-cell.month-col {
    justify-content: center;
  }

  .header-cell.events-col {
    justify-content: space-between;
  }

  /* Period selector (inline in header) */
  .period-selector {
    display: flex;
    background: #f9fafb;
    border-radius: 0.25rem;
    padding: 0.125rem;
    border: 1px solid #e5e7eb;
  }

  .period-btn {
    padding: 0.125rem 0.375rem;
    font-size: 0.5625rem;
    font-weight: 500;
    color: #6b7280;
    background: transparent;
    border: none;
    border-radius: 0.1875rem;
    cursor: pointer;
    transition: all 0.15s;
    text-transform: none;
    letter-spacing: 0;
  }

  .period-btn:hover {
    color: #374151;
    background: white;
  }

  .period-btn.active {
    color: #1d4ed8;
    background: white;
    font-weight: 600;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }

  /* Shared column widths */
  .month-col {
    width: 4.5rem;
    flex-shrink: 0;
    border-right: 1px solid #e5e7eb;
  }

  .projects-col {
    flex: 1;
    border-right: 1px dashed #e5e7eb;
    min-width: 0;
  }

  .events-col {
    flex: 1;
    min-width: 0;
  }

  .year-grid {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: white;
    border-radius: 0 0 0.375rem 0.375rem;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .month-row {
    flex: 1;
    display: flex;
    align-items: stretch;
    border-bottom: 1px solid #f3f4f6;
    min-height: 0;
    transition: all 0.15s;
  }

  .month-row:last-child {
    border-bottom: none;
  }

  .month-row.past {
    background: #fafafa;
  }

  .month-row.current {
    background: #eff6ff;
  }

  .month-row.future {
    background: #fdf9ff;
  }

  /* Drop target highlight on projects column only */
  .projects-col.drop-target {
    background: #dbeafe !important;
    box-shadow: inset 0 0 0 2px #3b82f6;
    border-radius: 0.25rem;
  }

  /* Month label column */
  .month-label {
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.02);
  }

  .month-link {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.125rem;
    padding: 0.25rem 0.375rem;
    background: none;
    border: none;
    cursor: pointer;
    border-radius: 0.25rem;
    transition: background 0.15s;
  }

  .month-link:hover {
    background: rgba(0, 0, 0, 0.05);
  }

  .month-name {
    font-size: 0.6875rem;
    font-weight: 700;
    color: #374151;
    letter-spacing: -0.01em;
  }

  .month-year {
    font-size: 0.5625rem;
    font-weight: 500;
    color: #9ca3af;
  }

  /* Projects and Events columns in data rows */
  .month-row .projects-col,
  .month-row .events-col {
    display: flex;
    flex-direction: column;
    padding: 0.25rem;
    min-height: 0;
    overflow: hidden;
  }

  /* Items grid - responsive columns via inline style */
  .items-grid {
    display: grid;
    /* grid-template-columns set via inline style for responsive column count */
    grid-auto-rows: min-content;
    gap: 0.25rem;
    flex: 1;
    min-height: 0;
    overflow: hidden;
    align-content: start;
  }

  /* Project chip */
  .project-chip {
    display: flex;
    align-items: center;
    gap: 0.1875rem;
    padding: 0.125rem 0.25rem;
    border-radius: 0.1875rem;
    font-size: 0.5625rem;
    background: #f9fafb;
    border: 1.5px solid var(--chip-border-color, #e5e7eb);
    cursor: grab;
    text-align: left;
    transition: all 0.15s;
    min-width: 0;
    overflow: hidden;
    flex-shrink: 0;
  }

  .chip-category {
    font-size: 0.5rem;
    flex-shrink: 0;
    line-height: 1;
  }

  .project-chip:hover {
    background: #f3f4f6;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  }

  .project-chip.dragging {
    opacity: 0.5;
    cursor: grabbing;
  }

  .chip-star {
    color: #facc15;
    font-size: 0.5rem;
    flex-shrink: 0;
  }

  .chip-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: #374151;
    min-width: 0;
  }

  .chip-span {
    flex-shrink: 0;
    font-size: 0.5rem;
    font-weight: 600;
    font-family: ui-monospace, monospace;
    color: #1d4ed8;
    background: rgba(59, 130, 246, 0.15);
    padding: 0 0.1875rem;
    border-radius: 0.125rem;
    letter-spacing: -0.02em;
  }

  /* Event chip - left accent bar style */
  .event-chip {
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.1875rem;
    padding: 0.1875rem 0.375rem;
    padding-left: 0.5rem;
    border-radius: 0.125rem;
    font-size: 0.5625rem;
    min-width: 0;
    overflow: hidden;
    flex-shrink: 0;
    background: #f3f4f6;
  }

  .event-chip::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: #9ca3af;
    border-radius: 0.125rem 0 0 0.125rem;
  }

  /* Event type accent colors (matching CalendarRail) */
  .event-chip.event-appointment::before {
    background: #60a5fa; /* blue-400 */
  }

  .event-chip.event-travel::before {
    background: #a78bfa; /* purple-400 */
  }

  .event-chip.event-event::before {
    background: #4ade80; /* green-400 */
  }

  .event-chip.event-deadline::before {
    background: #f87171; /* red-400 */
  }

  .event-date {
    font-weight: 600;
    flex-shrink: 0;
    color: #6b7280;
  }

  /* More chip - sized same as project chips */
  .more-chip {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.125rem 0.25rem;
    font-size: 0.5rem;
    font-weight: 500;
    color: #6b7280;
    background: transparent;
    border: 1px dashed #9ca3af;
    border-radius: 0.1875rem;
    cursor: default;
    transition: all 0.15s;
    white-space: nowrap;
    min-width: 0;
    overflow: hidden;
  }

  .more-chip:hover {
    border-color: #3b82f6;
    color: #3b82f6;
  }

  /* Empty cell */
  .empty-cell {
    font-size: 0.5rem;
    color: #d1d5db;
    padding: 0.125rem;
  }

  /* Inline add button (shown on hover) */
  .add-inline-btn {
    position: absolute;
    top: 0.125rem;
    right: 0.125rem;
    width: 1rem;
    height: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.625rem;
    font-weight: 600;
    color: #9ca3af;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 0.25rem;
    cursor: pointer;
    transition: all 0.15s;
    opacity: 0;
  }

  .month-row:hover .add-inline-btn {
    opacity: 1;
  }

  .add-inline-btn:hover {
    border-color: #3b82f6;
    color: #3b82f6;
    background: #eff6ff;
  }

  /* Make columns position relative for inline add button */
  .month-row .projects-col,
  .month-row .events-col {
    position: relative;
  }
</style>
